import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertActionLink,
  Button,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownList,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  MenuSearch,
  MenuSearchInput,
  MenuToggle,
  MenuToggleElement,
  PageSection,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Spinner,
  Tab,
  TabTitleText,
  Tabs,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import EllipsisVIcon from '@patternfly/react-icons/dist/esm/icons/ellipsis-v-icon';
import { AngleLeftIcon, LockIcon, SearchIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { Certificate, CertificateBucketKey } from '../../../types/models/Certificate';
import type { CertificatePlatform } from '../../../types/models/CertificatePlatform';
import type { ClientSummary } from '../../../types/models';
import type {
  ApiResponseCertificateList,
  ApiResponseCertificateSingle,
} from '../../../types/api/ApiResponseCertificate';
import { formatDateTime } from '../../../utils/dateFormat';
import CertificateActionComingSoonModal from './CertificateActionComingSoonModal';
import CreateCertificateModal from './CreateCertificateModal';
import CreateZerosslOrderModal from './CreateZerosslOrderModal';
import DeleteZerosslCertificateModal from './DeleteZerosslCertificateModal';
import RevokeCertificateModal from './RevokeCertificateModal';
import PlatformCaBundleTab from './PlatformCaBundleTab';
import { renderCertificateStatusLabel } from './CertificateOverviewTab';
import {
  CERTIFICATE_BUCKET_LABELS,
  CERTIFICATE_BUCKET_ORDER,
  classifyCertificateBucket,
  isCertificateBucketKey,
} from './certificateBuckets';

interface CertificatePlatformTabContentProps {
  platform: CertificatePlatform;
  clients: ClientSummary[] | null;
  isClientsLoading: boolean;
  canManageClientAssignment: boolean;
}

type KebabActionLabel = 'View details' | 'Renew' | 'Revoke' | 'Deploy' | 'Cancel order' | 'Delete';

// `request` and `revoked` certs surface only "View details" — no useful tooltip
// could explain "Renew/Revoke/Deploy on a cert that is being issued / already
// revoked", so hide rather than disable. Capability-driven Revoke (no platform
// support) is the only disabled-with-tooltip case; see the render site below.
// The exception: a not-yet-issued ZeroSSL order also gets its cleanup action
// (DELETE cancels upstream + removes validation records) — "Cancel order"
// while pending, "Delete" once the order failed / was cancelled upstream.
function visibleActionsForCertificate(certificate: Certificate): KebabActionLabel[] {
  const order = certificate.zerossl_order;
  if (order !== undefined && order.state !== 'issued') {
    return ['View details', order.state === 'pending' ? 'Cancel order' : 'Delete'];
  }
  switch (certificate.status) {
    case 'request':
    case 'revoked':
      return ['View details'];
    case 'ok':
    case 'expired':
    default:
      return ['View details', 'Renew', 'Revoke', 'Deploy'];
  }
}

const ASSIGN_LIST_MAX_HEIGHT = '320px';
const ASSIGN_ITEM_MAX_WIDTH = '480px';
const STATUS_FILTER_ALL_VALUE = 'all';

type KebabView = 'main' | 'assign';

interface RowKebabProps {
  certificate: Certificate;
  clients: ClientSummary[] | null;
  isClientsLoading: boolean;
  canManageClientAssignment: boolean;
  supportRevocation: boolean;
  onComingSoonAction: (label: string) => void;
  onAssignClient: (certificate: Certificate, clientID: number) => void;
  onUnassignClient: (certificate: Certificate) => void;
  onRevokeAction: (certificate: Certificate) => void;
  onViewDetails: (certificate: Certificate) => void;
  /** Cancel/delete a not-yet-issued ZeroSSL order (the row's cleanup DELETE). */
  onDeleteOrder: (certificate: Certificate) => void;
}

function RowKebab({
  certificate,
  clients,
  isClientsLoading,
  canManageClientAssignment,
  supportRevocation,
  onComingSoonAction,
  onAssignClient,
  onUnassignClient,
  onRevokeAction,
  onViewDetails,
  onDeleteOrder,
}: RowKebabProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [view, setView] = useState<KebabView>('main');
  const [assignSearchValue, setAssignSearchValue] = useState<string>('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const resetPanel = useCallback(() => {
    setView('main');
    setAssignSearchValue('');
  }, []);

  // Focus the search input when the assign panel becomes visible.
  // setTimeout(0) defers focus past the render that mounts the input so
  // ref.current is populated by the time we call .focus().
  useEffect(() => {
    if (view !== 'assign') {
      return undefined;
    }
    const handle = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [view]);

  // Force the dropdown's internal Popper to recompute its position whenever
  // the panel content height changes (`'main'` ↔ `'assign'`). PatternFly installs
  // a MutationObserver inside Popper that calls update() on DOM changes, but its
  // measurement can land before the new content has finished laying out —
  // leaving the assign panel detached from the kebab on rows near the viewport
  // edge. popper-core's eventListeners modifier already listens for window
  // resize and calls instance.update() on each one, so dispatching a resize
  // after setTimeout(0) forces a clean recompute (flip included) once layout
  // has settled. PopperOptions does not expose a popperRef we could call
  // update() on directly in 6.4.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 0);
    return () => window.clearTimeout(handle);
  }, [view]);

  const filteredClients = useMemo(() => {
    if (clients === null) {
      return [];
    }
    const q = assignSearchValue.trim().toLowerCase();
    const matched = q ? clients.filter((client) => client.name.toLowerCase().includes(q)) : clients;
    return [...matched].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [clients, assignSearchValue]);

  const closeAndReset = useCallback(() => {
    setIsOpen(false);
    // onOpenChange does not fire on programmatic close, so reset explicitly here.
    resetPanel();
  }, [resetPanel]);

  const renderAssignList = (): React.ReactNode => {
    if (clients === null) {
      return (
        <DropdownItem key="assign-status" isAriaDisabled>
          {isClientsLoading ? 'Loading clients…' : 'Failed to load clients'}
        </DropdownItem>
      );
    }
    if (clients.length === 0) {
      return (
        <DropdownItem key="assign-empty" isAriaDisabled>
          No clients available
        </DropdownItem>
      );
    }
    if (filteredClients.length === 0) {
      return (
        <DropdownItem key="assign-no-match" isAriaDisabled>
          No clients match your search
        </DropdownItem>
      );
    }
    return filteredClients.map((client) => (
      <DropdownItem
        key={client.id}
        isSelected={certificate.clientID === client.id}
        title={client.name}
        onClick={() => {
          closeAndReset();
          onAssignClient(certificate, client.id);
        }}
      >
        <span
          style={{
            display: 'inline-block',
            maxWidth: ASSIGN_ITEM_MAX_WIDTH,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            verticalAlign: 'middle',
          }}
        >
          {client.name}
        </span>
      </DropdownItem>
    ));
  };

  return (
    <Dropdown
      isOpen={isOpen}
      // onOpenChange fires on outside-click and on onOpenChangeKeys (default: tab/escape).
      // It does NOT fire on programmatic setIsOpen(false), so terminal click handlers
      // must call resetPanel() themselves (see closeAndReset).
      // Escape collapses the entire dropdown rather than going back one level from the
      // assign panel — accepted per spec; the next open starts on the main view because
      // resetPanel() runs here.
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          resetPanel();
        }
      }}
      popperProps={{ position: 'right' }}
      toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
        <MenuToggle
          ref={toggleRef}
          variant="plain"
          aria-label={`Actions for certificate ${certificate.id}`}
          isExpanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
          icon={<EllipsisVIcon />}
        />
      )}
    >
      {view === 'main' && (
        <DropdownList>
          {visibleActionsForCertificate(certificate).map((label) => {
            if (label === 'Revoke' && !supportRevocation) {
              return (
                <Tooltip key={label} content="Revocation is not supported on this certificate platform.">
                  <DropdownItem isAriaDisabled>{label}</DropdownItem>
                </Tooltip>
              );
            }
            return (
              <DropdownItem
                key={label}
                isDanger={label === 'Cancel order' || label === 'Delete'}
                onClick={() => {
                  closeAndReset();
                  if (label === 'View details') {
                    onViewDetails(certificate);
                  } else if (label === 'Revoke') {
                    onRevokeAction(certificate);
                  } else if (label === 'Cancel order' || label === 'Delete') {
                    onDeleteOrder(certificate);
                  } else {
                    onComingSoonAction(label);
                  }
                }}
              >
                {label}
              </DropdownItem>
            );
          })}
          {canManageClientAssignment && [
            <Divider key="assignment-divider" component="li" />,
            <DropdownItem
              key="assign-to"
              onClick={(event) => {
                // Navigation, not a terminal action: swap the panel in place.
                event.stopPropagation();
                setView('assign');
              }}
            >
              Assign to...
            </DropdownItem>,
            ...(certificate.clientID !== null
              ? [
                  <DropdownItem
                    key="unassign"
                    onClick={() => {
                      closeAndReset();
                      onUnassignClient(certificate);
                    }}
                  >
                    Unassign
                  </DropdownItem>,
                ]
              : []),
          ]}
        </DropdownList>
      )}
      {view === 'assign' && (
        <>
          <DropdownList>
            <DropdownItem
              key="back"
              icon={<AngleLeftIcon />}
              onClick={() => {
                setView('main');
              }}
            >
              Back
            </DropdownItem>
          </DropdownList>
          <MenuSearch>
            <MenuSearchInput>
              <SearchInput
                ref={searchInputRef}
                value={assignSearchValue}
                onChange={(_event, value) => setAssignSearchValue(value)}
                onClear={() => setAssignSearchValue('')}
                placeholder="Search clients..."
                aria-label="Search clients"
              />
            </MenuSearchInput>
          </MenuSearch>
          <Divider />
          <div style={{ maxHeight: ASSIGN_LIST_MAX_HEIGHT, overflowY: 'auto' }}>
            <DropdownList>{renderAssignList()}</DropdownList>
          </div>
        </>
      )}
    </Dropdown>
  );
}

function CertificatePlatformTabContent({
  platform,
  clients,
  isClientsLoading,
  canManageClientAssignment,
}: CertificatePlatformTabContentProps) {
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();
  const [certificates, setCertificates] = useState<Certificate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [comingSoonAction, setComingSoonAction] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [revokeTarget, setRevokeTarget] = useState<Certificate | null>(null);
  const [deleteOrderTarget, setDeleteOrderTarget] = useState<Certificate | null>(null);
  const [statusFilter, setStatusFilter] = useState<CertificateBucketKey | null>(null);
  const [isStatusOpen, setIsStatusOpen] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<string | number>('certificates');

  const clientById = useMemo(() => {
    if (clients === null) {
      return null;
    }
    const map = new Map<number, ClientSummary>();
    for (const client of clients) {
      map.set(client.id, client);
    }
    return map;
  }, [clients]);

  const fetchCertificates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/datacenters/certificates?certificate_platform_id=${platform.id}`);
      let body: ApiResponseCertificateList | null;
      try {
        body = (await response.json()) as ApiResponseCertificateList;
      } catch {
        body = null;
      }
      if (!response.ok || !body || body.status !== 'success' || !body.data) {
        const message =
          body?.message ?? `Failed to load certificates (HTTP ${response.status} ${response.statusText}).`.trim();
        setError(message);
        return;
      }
      setCertificates(body.data.certificates ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load certificates');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, platform.id]);

  const patchCertificateInPlace = useCallback((updated: Certificate) => {
    setCertificates((prev) => {
      if (prev === null) {
        return prev;
      }
      return prev.map((cert) => (cert.id === updated.id ? updated : cert));
    });
  }, []);

  const removeCertificateInPlace = useCallback((certificateId: number) => {
    setCertificates((prev) => {
      if (prev === null) {
        return prev;
      }
      return prev.filter((cert) => cert.id !== certificateId);
    });
  }, []);

  const handleAssignClient = useCallback(
    async (certificate: Certificate, clientID: number) => {
      const clientName = clientById?.get(clientID)?.name ?? `Client #${clientID}`;
      try {
        const response = await apiFetch(`/datacenters/certificates/${certificate.id}/client/${clientID}`, {
          method: 'PATCH',
        });
        let body: ApiResponseCertificateSingle | null;
        try {
          body = (await response.json()) as ApiResponseCertificateSingle;
        } catch {
          body = null;
        }
        if (!response.ok || !body || body.status !== 'success' || !body.data?.certificate) {
          const message =
            body?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim();
          throw new Error(message);
        }
        patchCertificateInPlace(body.data.certificate);
        addNotification({
          title: 'Certificate assigned',
          srTitle: 'Success alert',
          description: `${certificate.cn} is now assigned to ${clientName}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
      } catch (err) {
        addNotification({
          title: 'Could not assign certificate',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      }
    },
    [apiFetch, addNotification, setDrawerOpen, patchCertificateInPlace, clientById],
  );

  const handleUnassignClient = useCallback(
    async (certificate: Certificate) => {
      try {
        const response = await apiFetch(`/datacenters/certificates/${certificate.id}/client`, {
          method: 'DELETE',
        });
        let body: ApiResponseCertificateSingle | null;
        try {
          body = (await response.json()) as ApiResponseCertificateSingle;
        } catch {
          body = null;
        }
        if (!response.ok || !body || body.status !== 'success' || !body.data?.certificate) {
          const message =
            body?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim();
          throw new Error(message);
        }
        patchCertificateInPlace(body.data.certificate);
        addNotification({
          title: 'Certificate unassigned',
          srTitle: 'Success alert',
          description: `${certificate.cn} is no longer assigned to a client.`,
          variant: 'success',
        });
        setDrawerOpen(true);
      } catch (err) {
        addNotification({
          title: 'Could not unassign certificate',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      }
    },
    [apiFetch, addNotification, setDrawerOpen, patchCertificateInPlace],
  );

  const handleCertificateCreated = useCallback((created: Certificate) => {
    setCertificates((prev) => {
      if (prev === null) {
        return [created];
      }
      return [created, ...prev];
    });
    setPage(1);
  }, []);

  useEffect(() => {
    void fetchCertificates();
  }, [fetchCertificates]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  // Hydrate the status filter from the URL. The tab content is mounted
  // lazily, so on first mount the URL may already carry a `status=` from a
  // dashboard click-through. Re-running on every searchParams change also
  // covers the back/forward case where another component rewrites the
  // query string.
  useEffect(() => {
    const raw = searchParams.get('status');
    if (raw !== null && isCertificateBucketKey(raw)) {
      setStatusFilter(raw);
      setPage(1);
    } else {
      setStatusFilter(null);
    }
  }, [searchParams]);

  const handleStatusFilterChange = useCallback(
    (bucket: CertificateBucketKey | null) => {
      setStatusFilter(bucket);
      setPage(1);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (bucket === null) {
            next.delete('status');
          } else {
            next.set('status', bucket);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const filtered = useMemo(() => {
    if (certificates === null) {
      return [];
    }
    const q = debouncedSearch.trim().toLowerCase();
    return certificates.filter((cert) => {
      if (q && !cert.cn.toLowerCase().includes(q) && !cert.sans.join(' ').toLowerCase().includes(q)) {
        return false;
      }
      if (statusFilter !== null && classifyCertificateBucket(cert) !== statusFilter) {
        return false;
      }
      return true;
    });
  }, [certificates, debouncedSearch, statusFilter]);

  const paginated = useMemo(() => {
    const start = (page - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, page, perPage]);

  // Deleting the last row of the last page (or any shrink of the filtered
  // set) can leave `page` pointing past the end — clamp it back so the table
  // never renders an empty page with no way to tell why.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filtered.length / perPage));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filtered.length, perPage, page]);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPage: number) => {
    setPage(newPage);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchValue('');
    setDebouncedSearch('');
    setPage(1);
  };

  const clearAllFilters = useCallback(() => {
    setSearchValue('');
    setDebouncedSearch('');
    setStatusFilter(null);
    setPage(1);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('status');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const renderClientCell = (cert: Certificate): React.ReactNode => {
    if (cert.clientID === null) {
      return (
        <Label color="orange" isCompact>
          Unassigned
        </Label>
      );
    }
    if (clientById === null) {
      if (isClientsLoading) {
        return <Spinner size="sm" aria-label="Loading client name" />;
      }
      return `Client #${cert.clientID}`;
    }
    const client = clientById.get(cert.clientID);
    if (client !== undefined) {
      return client.name;
    }
    return `Client #${cert.clientID}`;
  };

  const showInitialSpinner = loading && certificates === null;
  const hasActiveFilters = debouncedSearch !== '' || statusFilter !== null;
  // The create-request body shape is genuinely type-specific (the backend
  // branches POST /datacenters/certificates on the platform's type), so this
  // is the one place a type check is correct — capability flags cover
  // *optional driver features*, not the wire contract of the create call.
  const isZerosslPlatform = platform.type === 'zerossl';

  let body: React.ReactNode;
  if (showInitialSpinner) {
    body = (
      <EmptyState
        titleText="Loading certificates…"
        icon={() => <Spinner size="lg" aria-label="Loading certificates" />}
        variant="lg"
        headingLevel="h2"
      >
        <EmptyStateBody>Fetching certificates from {platform.name}.</EmptyStateBody>
      </EmptyState>
    );
  } else if (error !== null) {
    body = null;
  } else if (certificates !== null && certificates.length === 0) {
    body = (
      <EmptyState titleText="No certificates yet" icon={LockIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>
          {isZerosslPlatform
            ? 'Order your first certificate — pending orders and issued certificates will appear here.'
            : 'Issued certificates will appear here once they are synced from the platform.'}
        </EmptyStateBody>
      </EmptyState>
    );
  } else if (certificates !== null && filtered.length === 0 && hasActiveFilters) {
    body = (
      <EmptyState titleText="No certificates match your filters" icon={SearchIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>Try a different search term, change the status filter, or clear filters.</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={clearAllFilters}>
              Clear filters
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  } else if (certificates !== null) {
    body = (
      <Table aria-label={`Certificates issued by ${platform.name}`} variant="compact">
        <Thead>
          <Tr>
            <Th>Common name</Th>
            <Th>SANs</Th>
            {canManageClientAssignment && <Th>Client</Th>}
            <Th>Valid from</Th>
            <Th>Valid to</Th>
            <Th>Status</Th>
            <Th aria-label="Actions" />
          </Tr>
        </Thead>
        <Tbody>
          {paginated.map((cert) => (
            <Tr key={cert.id}>
              <Td dataLabel="Common name">
                <Link to={`/datacenter/certificates/${cert.id}`}>{cert.cn}</Link>
              </Td>
              <Td dataLabel="SANs">
                {cert.sans.length === 0 ? '—' : <div style={{ whiteSpace: 'pre-line' }}>{cert.sans.join('\n')}</div>}
              </Td>
              {canManageClientAssignment && <Td dataLabel="Client">{renderClientCell(cert)}</Td>}
              <Td dataLabel="Valid from">{formatDateTime(cert.not_before)}</Td>
              <Td dataLabel="Valid to">{formatDateTime(cert.not_after)}</Td>
              <Td dataLabel="Status">{renderCertificateStatusLabel(cert)}</Td>
              <Td dataLabel="Actions" isActionCell>
                <RowKebab
                  certificate={cert}
                  clients={clients}
                  isClientsLoading={isClientsLoading}
                  canManageClientAssignment={canManageClientAssignment}
                  supportRevocation={platform.support_revocation}
                  onComingSoonAction={(label) => setComingSoonAction(label)}
                  onAssignClient={handleAssignClient}
                  onUnassignClient={handleUnassignClient}
                  onRevokeAction={(target) => setRevokeTarget(target)}
                  onViewDetails={(target) => navigate(`/datacenter/certificates/${target.id}`)}
                  onDeleteOrder={(target) => setDeleteOrderTarget(target)}
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  } else {
    body = null;
  }

  const statusToggleLabel = statusFilter === null ? 'All statuses' : CERTIFICATE_BUCKET_LABELS[statusFilter];

  const certificatesSection = (
    <>
      <Toolbar id={`certificates-toolbar-${platform.id}`}>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by common name or SAN..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={clearSearch}
              aria-label="Search certificates"
            />
          </ToolbarItem>
          <ToolbarItem>
            <Select
              isOpen={isStatusOpen}
              selected={statusFilter ?? STATUS_FILTER_ALL_VALUE}
              onOpenChange={(open) => setIsStatusOpen(open)}
              onSelect={(_event, value) => {
                setIsStatusOpen(false);
                if (value === STATUS_FILTER_ALL_VALUE) {
                  handleStatusFilterChange(null);
                  return;
                }
                if (typeof value === 'string' && isCertificateBucketKey(value)) {
                  handleStatusFilterChange(value);
                }
              }}
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsStatusOpen((open) => !open)}
                  isExpanded={isStatusOpen}
                  aria-label="Filter certificates by status"
                  style={{ minWidth: '180px' }}
                >
                  {statusToggleLabel}
                </MenuToggle>
              )}
              shouldFocusToggleOnSelect
            >
              <SelectList>
                <SelectOption value={STATUS_FILTER_ALL_VALUE}>All statuses</SelectOption>
                {CERTIFICATE_BUCKET_ORDER.map((bucket) => (
                  <SelectOption key={bucket} value={bucket}>
                    {CERTIFICATE_BUCKET_LABELS[bucket]}
                  </SelectOption>
                ))}
              </SelectList>
            </Select>
          </ToolbarItem>
          {searchValue !== '' && (
            <ToolbarItem>
              <Button variant="link" onClick={clearSearch}>
                Clear
              </Button>
            </ToolbarItem>
          )}
          <ToolbarGroup align={{ default: 'alignEnd' }}>
            <ToolbarItem>
              <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
                {isZerosslPlatform ? 'Order certificate' : 'Create certificate'}
              </Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button
                variant="secondary"
                onClick={() => void fetchCertificates()}
                isLoading={loading && certificates !== null}
                isDisabled={loading && certificates !== null}
              >
                Refresh
              </Button>
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filtered.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
                variant="top"
                isCompact
              />
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>

      {error !== null && (
        <Alert
          variant="danger"
          isInline
          title="Failed to load certificates"
          actionLinks={<AlertActionLink onClick={() => void fetchCertificates()}>Retry</AlertActionLink>}
        >
          {error}
        </Alert>
      )}

      {body}

      {certificates !== null && filtered.length > 0 && (
        <Pagination
          itemCount={filtered.length}
          perPage={perPage}
          page={page}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          variant="bottom"
        />
      )}

      <CertificateActionComingSoonModal actionLabel={comingSoonAction} onClose={() => setComingSoonAction(null)} />

      {/* The create call's body shape follows the platform type: ZeroSSL
          platforms place an asynchronous order (domains + validity), every
          other type submits the OPNsense CSR form. The order modal is
          mounted only while open so each order starts from a fresh state. */}
      {isZerosslPlatform ? (
        isCreateModalOpen && (
          <CreateZerosslOrderModal
            platformId={platform.id}
            platformName={platform.name}
            onClose={() => setIsCreateModalOpen(false)}
            onCreated={handleCertificateCreated}
            onPairChange={patchCertificateInPlace}
            onOrderMissing={removeCertificateInPlace}
          />
        )
      ) : (
        <CreateCertificateModal
          isOpen={isCreateModalOpen}
          platformId={platform.id}
          platformName={platform.name}
          onClose={() => setIsCreateModalOpen(false)}
          onCreated={handleCertificateCreated}
        />
      )}

      {deleteOrderTarget !== null && deleteOrderTarget.zerossl_order !== undefined && (
        <DeleteZerosslCertificateModal
          certificate={deleteOrderTarget}
          orderState={deleteOrderTarget.zerossl_order.state}
          onClose={() => setDeleteOrderTarget(null)}
          onDeleted={removeCertificateInPlace}
        />
      )}

      <RevokeCertificateModal
        certificate={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onRevoked={(updated) => {
          patchCertificateInPlace(updated);
          setRevokeTarget(null);
        }}
      />
    </>
  );

  // Platforms whose driver cannot serve a CA bundle keep the flat layout —
  // a single-tab strip would only add noise. `support_ca_bundle` is the
  // backend-computed driver capability flag (e.g. false on ACME platforms,
  // where intermediates rotate and no single platform CA exists).
  if (!platform.support_ca_bundle) {
    return <PageSection hasBodyWrapper={false}>{certificatesSection}</PageSection>;
  }

  return (
    <PageSection hasBodyWrapper={false}>
      <Tabs
        isSubtab
        // The CA bundle is a live (slow) read of the upstream appliance:
        // mount it on first activation only, then keep it mounted so the
        // fetched chain survives tab switches (manual Refresh inside).
        mountOnEnter
        activeKey={activeSection}
        onSelect={(_event, eventKey) => setActiveSection(eventKey)}
        aria-label={`${platform.name} sections`}
      >
        <Tab eventKey="certificates" title={<TabTitleText>Certificates</TabTitleText>}>
          {certificatesSection}
        </Tab>
        <Tab eventKey="ca-bundle" title={<TabTitleText>CA Bundle</TabTitleText>}>
          <PlatformCaBundleTab platform={platform} />
        </Tab>
      </Tabs>
    </PageSection>
  );
}

export default CertificatePlatformTabContent;
